import type { FC, PropsWithChildren } from 'react';
import { useMemo, useState } from 'react';

import { Dialog } from '@opendoor/bricks-next';

import type { ReauthOptions, ReauthResult } from 'platform/components/ReauthContext';
import { ReauthContext } from 'platform/components/ReauthContext';
import { ReauthForm } from 'platform/components/ReauthForm';

import { RAILS_ENV } from 'shared/helpers/globals';

/** Provides ReauthContext so that components can call showReauth from anywhere.  */
export const ReauthProvider: FC<PropsWithChildren> = ({ children }) => {
  // The last caller of showReauth, or undefined if never called.
  const [caller, setCaller] = useState<ReauthOptions>();
  const contextValue: ReauthResult = useMemo(
    () => ({
      showReauth: (options) =>
        setCaller((prevCaller) => {
          if (prevCaller) {
            if (RAILS_ENV !== 'production') {
              // eslint-disable-next-line no-console -- dev only consoles
              console.warn(
                'showReauth called twice! The second showReauth call will be ignored. This warning only shows up in non-production builds.',
              );
            }
            return prevCaller;
          }
          return options;
        }),
    }),
    [setCaller],
  );

  //Condition to whether show or hide the modal
  const isModalOpen = caller !== undefined;

  return (
    <ReauthContext.Provider value={contextValue}>
      {children}
      <Dialog
        analyticsName="cake-reauth-modal"
        trackImpression
        isOpen={isModalOpen}
        setIsOpen={(isOpen) => {
          if (!isOpen) {
            setCaller(undefined);
            caller?.onCancel();
          }
        }}
      >
        <Dialog.Content title="" description="">
          <ReauthForm
            title={caller?.title || 'Sign in again to continue'}
            onSuccess={() => {
              setCaller(undefined);
              caller?.onSuccess();
            }}
          />
        </Dialog.Content>
      </Dialog>
    </ReauthContext.Provider>
  );
};
